import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import supabase from '../../supabase/supabase';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

const LOGO_URL = "https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780693784/BOTG_Logo_o9fcqe.png";

export default function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';

  const [resendTimer, setResendTimer] = useState(() => {
    const saved = sessionStorage.getItem('verifyTimer');
    if (saved) {
      const diff = Math.floor((Date.now() - parseInt(saved)) / 1000);
      return diff < 60 ? 60 - diff : 0;
    }
    return 60;
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!email) {
      navigate('/login');
      return;
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      sessionStorage.setItem('verifyTimer', (Date.now() - (60 - resendTimer) * 1000).toString());
      const interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      sessionStorage.removeItem('verifyTimer');
    }
  }, [resendTimer]);

  const handleResend = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    setError(null);
    setMessage(null);
    
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          // Set the redirect exactly to the whitelisted /verified URL
          emailRedirectTo: `https://bookotg.com/verified`
        }
      });
      
      if (resendError) throw resendError;
      
      setMessage('Verification link has been resent!');
      setResendTimer(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-light flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-8 cursor-pointer" onClick={() => navigate('/')}>
          <img src={LOGO_URL} alt="BookOTG" className="h-24 w-auto object-contain" />
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-12 px-8 shadow-2xl rounded-[2.5rem] border border-gray-100 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-brand-primary"></div>
          
          <div className="w-24 h-24 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border-4 border-white ring-1 ring-gray-100">
            <SafeIcon name="Mail" className="text-4xl" />
          </div>
          
          <h2 className="text-2xl font-black text-brand-dark mb-4 tracking-tight">Verify Your Account</h2>
          
          <p className="text-gray-500 font-medium mb-6 leading-relaxed">
            Thank you for signing up! We've sent a secure verification link to <br/>
            <span className="font-bold text-brand-dark">{email}</span>
          </p>

          <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 mb-8 text-left flex items-start gap-3">
            <SafeIcon name="AlertCircle" className="text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-orange-800 font-medium">
              If you don't see the email in your inbox, please check your <strong>spam</strong> or <strong>junk</strong> folder.
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-xl text-sm font-bold">
              {error}
            </div>
          )}
          
          {message && (
            <div className="mb-6 bg-green-50 text-green-600 p-4 rounded-xl text-sm font-bold">
              {message}
            </div>
          )}

          <div className="space-y-4">
            <Button
              onClick={handleResend}
              disabled={resendTimer > 0 || loading}
              className="w-full py-4 text-sm font-black rounded-xl"
            >
              {loading ? 'Sending...' : resendTimer > 0 ? `Resend Verification Email (${resendTimer}s)` : 'Resend Verification Email'}
            </Button>

            <button
              onClick={() => navigate('/login')}
              className="text-sm font-bold text-gray-400 hover:text-brand-dark transition-colors"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}